import React, { useState } from "react"
import PropTypes from "prop-types"
import {
  Banner,
  Button,
  Form,
  FormLayout,
  Modal,
  TextField,
} from "@shopify/polaris"
import { useTranslation } from "react-i18next"

/**
 * Centralized ServiceNow configuration modal
 * Allows configuring ServiceNow credentials once for the entire platform
 */
const ServiceNowConfigModal = ({ isOpen, onClose, onSave }) => {
  const { t } = useTranslation()

  const [config, setConfig] = useState({
    serviceNowUrl: "",
    username: "",
    password: "",
  })

  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const handleChange = (value, field) => {
    setConfig((prev) => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!config.serviceNowUrl) {
      newErrors.serviceNowUrl = t(
        "developer_portal:connectors.service_now.config.errors.url_required"
      )
    } else if (!config.serviceNowUrl.startsWith("https://")) {
      newErrors.serviceNowUrl = t(
        "developer_portal:connectors.service_now.config.errors.url_invalid"
      )
    }

    if (!config.username) {
      newErrors.username = t(
        "developer_portal:connectors.service_now.config.errors.username_required"
      )
    }

    if (!config.password) {
      newErrors.password = t(
        "developer_portal:connectors.service_now.config.errors.password_required"
      )
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    try {
      await onSave(config)
      // Reset form after successful save
      setConfig({
        serviceNowUrl: "",
        username: "",
        password: "",
      })
    } catch (error) {
      console.error("Error saving ServiceNow config:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setConfig({
      serviceNowUrl: "",
      username: "",
      password: "",
    })
    setErrors({})
    onClose()
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={t("developer_portal:connectors.service_now.config.title")}
      primaryAction={{
        content: t("developer_portal:connectors.service_now.config.save"),
        onAction: handleSubmit,
        loading: isLoading,
      }}
      secondaryActions={[
        {
          content: t("common:cancel"),
          onAction: handleClose,
        },
      ]}
    >
      <Modal.Section>
        <Banner status="info">
          {t("developer_portal:connectors.service_now.config.info")}
        </Banner>
        <Form onSubmit={handleSubmit}>
          <FormLayout>
            <TextField
              label={t(
                "developer_portal:connectors.service_now.config.fields.url"
              )}
              value={config.serviceNowUrl}
              onChange={(value) => handleChange(value, "serviceNowUrl")}
              placeholder="https://your-instance.service-now.com"
              error={errors.serviceNowUrl}
              autoComplete="off"
            />
            <TextField
              label={t(
                "developer_portal:connectors.service_now.config.fields.username"
              )}
              value={config.username}
              onChange={(value) => handleChange(value, "username")}
              error={errors.username}
              autoComplete="username"
            />
            <TextField
              label={t(
                "developer_portal:connectors.service_now.config.fields.password"
              )}
              type="password"
              value={config.password}
              onChange={(value) => handleChange(value, "password")}
              error={errors.password}
              autoComplete="current-password"
            />
          </FormLayout>
        </Form>
      </Modal.Section>
    </Modal>
  )
}

ServiceNowConfigModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
}

export default ServiceNowConfigModal
